// src/components/ReservationsDashboard.js - VERSION RICH UI
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import CancellationModal from './CancellationModal';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Marcellus&family=DM+Sans:wght@300;400;500;600&display=swap');

  .resadash-wrap {
    font-family: 'DM Sans', sans-serif;
    color: #1a1a1a;
  }

  /* ── HEADER ── */
  .resadash-header {
    background: linear-gradient(135deg, #065f46 0%, #047857 100%);
    border-radius: 14px;
    padding: 22px 26px;
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    position: relative;
    overflow: hidden;
  }
  .resadash-header::after {
    content: 'ر';
    position: absolute;
    right: 16px; bottom: -12px;
    font-size: 80px;
    color: rgba(255,255,255,0.04);
    font-family: 'Marcellus', serif;
    pointer-events: none;
  }
  .resadash-header-left {}
  .resadash-title {
    font-family: 'Marcellus', serif;
    font-size: 19px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 4px;
  }
  .resadash-sub {
    font-size: 12px;
    color: rgba(255,255,255,0.55);
  }
  .resadash-stats {
    display: flex;
    gap: 12px;
  }
  .resadash-stat {
    background: rgba(255,255,255,0.1);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    padding: 10px 16px;
    text-align: center;
    min-width: 80px;
  }
  .resadash-stat-num {
    font-family: 'Marcellus', serif;
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  .resadash-stat-label {
    font-size: 10px;
    color: rgba(255,255,255,0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 3px;
  }

  /* ── TOAST ── */
  .resadash-toast {
    padding: 11px 16px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 18px;
  }
  .resadash-toast.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
  .resadash-toast.error   { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }

  /* ── FILTRES ── */
  .resadash-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .filter-pill {
    padding: 7px 14px;
    border: 1.5px solid #e0d9ce;
    border-radius: 20px;
    background: #faf9f7;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #666;
    cursor: pointer;
    transition: all 0.15s;
  }
  .filter-pill:hover { border-color: #065f46; color: #065f46; }
  .filter-pill.active { background: #065f46; border-color: #065f46; color: #fff; }
  .filter-pill.archive {
    background: transparent;
    border-color: #e0d9ce;
    color: #aaa;
  }
  .filter-pill.archive.active { background: #f3f4f6; border-color: #9ca3af; color: #555; }

  /* ── CARDS ── */
  .resa-card {
    background: #fff;
    border: 1.5px solid #e8e2d9;
    border-radius: 14px;
    padding: 20px;
    margin-bottom: 12px;
    transition: box-shadow 0.2s;
  }
  .resa-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.06); }
  .resa-card.en_attente { border-left: 4px solid #f59e0b; }
  .resa-card.confirmé   { border-left: 4px solid #10b981; }
  .resa-card.refusé     { border-left: 4px solid #ef4444; }
  .resa-card.annulé     { border-left: 4px solid #9ca3af; }
  .resa-card.expiré     { border-left: 4px solid #d1d5db; }

  .resa-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }
  .resa-person {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .resa-avatar {
    width: 38px; height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #065f46, #047857);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    flex-shrink: 0;
  }
  .resa-person-name {
    font-weight: 700;
    font-size: 15px;
    color: #1a1a1a;
  }
  .resa-person-sub {
    font-size: 11px;
    color: #aaa;
    margin-top: 1px;
  }

  /* Status badges */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }
  .status-badge.en_attente { background: #fef3c7; color: #92400e; }
  .status-badge.confirmé   { background: #d1fae5; color: #065f46; }
  .status-badge.refusé     { background: #fee2e2; color: #991b1b; }
  .status-badge.annulé     { background: #f3f4f6; color: #6b7280; }
  .status-badge.expiré     { background: #f3f4f6; color: #9ca3af; }

  /* Détails */
  .resa-details {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  .resa-detail-item {}
  .resa-detail-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #bbb;
    font-weight: 600;
    margin-bottom: 2px;
  }
  .resa-detail-val {
    font-size: 13px;
    font-weight: 700;
    color: #1a1a1a;
  }

  /* Countdown */
  .resa-countdown {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
  }
  .resa-countdown.today  { background: #d1fae5; color: #065f46; }
  .resa-countdown.soon   { background: #fef3c7; color: #92400e; }
  .resa-countdown.future { background: #eff6ff; color: #1d4ed8; }
  .resa-countdown.past   { background: #f3f4f6; color: #9ca3af; }

  /* Messages */
  .resa-message-box {
    background: #f7f4ef;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    color: #555;
    margin-bottom: 10px;
    font-style: italic;
  }
  .resa-message-box.refus {
    background: #fef2f2;
    color: #991b1b;
    font-style: normal;
  }
  .resa-video-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 12px;
    color: #065f46;
    margin-bottom: 10px;
  }

  /* Actions */
  .resa-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding-top: 12px;
    border-top: 1px solid #f7f4ef;
  }
  .btn-confirm-resa {
    padding: 8px 16px;
    background: linear-gradient(135deg, #065f46 0%, #047857 100%);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(6,95,70,0.25);
  }
  .btn-confirm-resa:hover { transform: translateY(-1px); }
  .btn-refuse-resa {
    padding: 8px 16px;
    background: #fee2e2;
    color: #dc2626;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-refuse-resa:hover { background: #fecaca; }
  .btn-cancel-resa {
    padding: 8px 16px;
    background: #f3f4f6;
    color: #6b7280;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-cancel-resa:hover { background: #e5e7eb; }

  /* Footer card */
  .resa-card-footer {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #ccc;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #f7f4ef;
  }

  /* Empty */
  .resadash-empty {
    text-align: center;
    padding: 48px 20px;
    background: #fff;
    border: 1px solid #e8e2d9;
    border-radius: 14px;
  }
  .resadash-empty-icon { font-size: 40px; margin-bottom: 14px; opacity: 0.3; }
  .resadash-empty h3 {
    font-family: 'Marcellus', serif;
    font-size: 18px;
    color: #065f46;
    margin-bottom: 8px;
  }
  .resadash-empty p { font-size: 13px; color: #aaa; }

  /* Loading */
  .resadash-loading {
    text-align: center;
    padding: 40px;
    color: #999;
    font-size: 14px;
  }
  .spinner-ring {
    width: 36px; height: 36px;
    border: 3px solid #e0d9ce;
    border-top-color: #047857;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 12px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const STATUS_CONFIG = {
  en_attente: { label: 'En attente', dot: '⏳' },
  confirmé:   { label: 'Confirmé',   dot: '✅' },
  refusé:     { label: 'Refusé',     dot: '❌' },
  annulé:     { label: 'Annulé',     dot: '🚫' },
  expiré:     { label: 'Expiré',     dot: '⏰' },
};

const getCountdown = (date) => {
  const d = new Date(date);
  const today = new Date();
  d.setHours(0,0,0,0); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0)  return { label: 'Passé',       cls: 'past' };
  if (diff === 0) return { label: "Aujourd'hui", cls: 'today' };
  if (diff === 1) return { label: 'Demain',      cls: 'soon' };
  return { label: `Dans ${diff}j`, cls: 'future' };
};

const getInitials = (name = '') => {
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : name.slice(0,2).toUpperCase() || '?';
};

const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

export default function ReservationsDashboard({ token: propToken }) {
  const [reservations, setReservations] = useState([]);
  const [userType, setUserType] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [message, setMessage] = useState('');
  const [token, setToken] = useState(propToken || null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(false);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 4000); };

  useEffect(() => {
    if (!token) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.access_token) setToken(session.access_token);
      });
    }
  }, [propToken, token]);

  useEffect(() => {
    if (token) fetchReservations();
  }, [token]);

  // Auto-refresh toutes les 30 secondes
  useEffect(() => {
    if (!token) return;
    const isExpired = (t) => {
      try { return JSON.parse(atob(t.split('.')[1])).exp * 1000 < Date.now(); }
      catch { return true; }
    };
    const interval = setInterval(() => {
      if (isExpired(token)) return;
      fetchReservations();
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchReservations = async () => {
    if (!token) { showMsg('⚠️ Token manquant'); setLoading(false); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) { setReservations(data.reservations || []); setUserType(data.user_type); }
      else showMsg('❌ Erreur lors du chargement');
    } catch { showMsg('❌ Erreur de connexion'); }
    finally { setLoading(false); }
  };

  const handleConfirm = async (id) => {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${id}/confirm`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) { showMsg('✅ Réservation confirmée'); fetchReservations(); }
      else showMsg(`❌ ${data.error}`);
    } catch { showMsg('❌ Erreur lors de la confirmation'); }
    finally { setConfirming(false); confirmingRef.current = false; }
  };

  const handleRefuse = async (id) => {
    const motif = window.prompt('Motif du refus (optionnel) :');
    if (motif === null) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${id}/refuse`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ motif_refus: motif })
      });
      const data = await res.json();
      if (data.success) { showMsg('✅ Réservation refusée'); fetchReservations(); }
      else showMsg(`❌ ${data.error}`);
    } catch { showMsg('❌ Erreur lors du refus'); }
  };

  const handleCancel = (reservation) => {
    setSelectedReservation(reservation);
    setShowCancelModal(true);
  };

  const confirmCancel = async (motif) => {
    if (!selectedReservation) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${selectedReservation.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motif_annulation: motif })
      });
      const data = await res.json();
      if (data.success) {
        showMsg(`✅ Annulé.${data.credit_rembourse ? ' Crédit remboursé.' : ''}`);
        setShowCancelModal(false);
        setSelectedReservation(null);
        fetchReservations();
      } else showMsg(`❌ ${data.error || 'Erreur'}`);
    } catch { showMsg('❌ Erreur lors de l\'annulation'); }
  };

  const isProf = userType === 'prof' || userType === 'both';

  const archivedCount = reservations.filter(r => {
    const end = new Date(`${r.date}T${r.heure_fin}`);
    return end < new Date() && r.statut !== 'en_attente';
  }).length;

  const filtered = reservations.filter(r => {
    if (filter !== 'all' && r.statut !== filter) return false;
    if (!showArchived) {
      const end = new Date(`${r.date}T${r.heure_fin}`);
      if (end < new Date() && r.statut !== 'en_attente') return false;
    }
    return true;
  });

  // Helpers timezone : privilégier start_utc/end_utc (fournis par backend depuis étape 3)
  const getCoursStart = (r) => {
    if (r.start_utc) return new Date(r.start_utc);
    if (!r.date || !r.heure_debut) return null;
    return new Date(`${r.date}T${r.heure_debut}`);
  };
  const getCoursEnd = (r) => {
    if (r.end_utc) return new Date(r.end_utc);
    if (!r.date || !r.heure_fin) return null;
    return new Date(`${r.date}T${r.heure_fin}`);
  };

  const upcomingConfirmed = reservations.filter(r => {
    if (r.statut !== 'confirmé') return false;
    const start = getCoursStart(r);
    return start && start.getTime() > Date.now();
  }).length;

  const pending = reservations.filter(r => r.statut === 'en_attente').length;

  if (loading) return (
    <div className="resadash-wrap">
      <style>{styles}</style>
      <div className="resadash-loading">
        <div className="spinner-ring" />
        Chargement des réservations…
      </div>
    </div>
  );

  return (
    <div className="resadash-wrap">
      <style>{styles}</style>

      {/* Header */}
      <div className="resadash-header">
        <div className="resadash-header-left">
          <div className="resadash-title">
            {isProf ? '📋 Demandes de réservation' : '📋 Mes réservations'}
          </div>
          <div className="resadash-sub">
            {isProf ? 'Gérez les demandes de cours de vos élèves' : 'Suivez vos demandes et cours programmés'}
          </div>
        </div>
        <div className="resadash-stats">
          <div className="resadash-stat">
            <div className="resadash-stat-num">{upcomingConfirmed}</div>
            <div className="resadash-stat-label">À venir</div>
          </div>
          <div className="resadash-stat">
            <div className="resadash-stat-num">{pending}</div>
            <div className="resadash-stat-label">En attente</div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {message && (
        <div className={`resadash-toast ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Filtres */}
      <div className="resadash-filters">
        {[
          { key: 'all',       label: 'Toutes' },
          { key: 'en_attente', label: 'En attente' },
          { key: 'confirmé',  label: 'Confirmées' },
          { key: 'refusé',    label: 'Refusées' },
          { key: 'annulé',    label: 'Annulées' },
        ].map(f => (
          <button key={f.key} className={`filter-pill ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
        {archivedCount > 0 && (
          <button className={`filter-pill archive ${showArchived ? 'active' : ''}`} onClick={() => setShowArchived(!showArchived)}>
            📁 Historique ({archivedCount})
          </button>
        )}
      </div>

      {/* Liste */}
      {filtered.length === 0 ? (
        <div className="resadash-empty">
          <div className="resadash-empty-icon">📋</div>
          <h3>Aucune réservation</h3>
          <p>
            {isProf
              ? 'Les demandes de cours apparaîtront ici quand les élèves vous contacteront.'
              : 'Vos demandes apparaîtront ici après avoir réservé avec un professeur.'}
          </p>
        </div>
      ) : (
        filtered.map(r => {
          const statusCfg = STATUS_CONFIG[r.statut] || STATUS_CONFIG['en_attente'];
          const personName = isProf
            ? `${r.eleves?.prenom || ''} ${r.eleves?.nom || 'Élève'}`.trim()
            : r.profs?.nom || 'Professeur';
          const countdown = r.statut === 'confirmé' ? getCountdown(r.date) : null;
          const startCours = getCoursStart(r);
          const endCours = getCoursEnd(r);
          const isFuture = startCours && startCours.getTime() > Date.now();
          const isEndFuture = endCours && endCours.getTime() > Date.now();

          return (
            <div key={r.id} className={`resa-card ${r.statut}`}>
              {/* Top */}
              <div className="resa-card-top">
                <div className="resa-person">
                  <div className="resa-avatar">{getInitials(personName)}</div>
                  <div>
                    <div className="resa-person-name">{personName}</div>
                    <div className="resa-person-sub">{formatDate(r.date)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {countdown && (
                    <span className={`resa-countdown ${countdown.cls}`}>{countdown.label}</span>
                  )}
                  <span className={`status-badge ${r.statut}`}>
                    {statusCfg.dot} {statusCfg.label}
                  </span>
                </div>
              </div>

              {/* Détails */}
              <div className="resa-details">
                <div className="resa-detail-item">
                  <div className="resa-detail-label">Heure</div>
                  <div className="resa-detail-val">🕒 {r.heure_debut?.substring(0,5)} – {r.heure_fin?.substring(0,5)}</div>
                </div>
                <div className="resa-detail-item">
                  <div className="resa-detail-label">Durée</div>
                  <div className="resa-detail-val">{r.duree_minutes || 30} min</div>
                </div>
              </div>

              {/* Message élève */}
              {r.message_eleve && (
                <div className="resa-message-box">💬 {r.message_eleve}</div>
              )}

              {/* Motif refus */}
              {r.motif_refus && (
                <div className="resa-message-box refus">⚠️ {r.motif_refus}</div>
              )}

              {/* Cours confirmé */}
              {r.statut === 'confirmé' && r.cours_id && (
                <div className="resa-video-box">
                  🎥 <strong>Cours confirmé</strong> — Le lien sera disponible 15 min avant le début.
                </div>
              )}

              {/* Actions */}
              <div className="resa-actions">
                {isProf && r.statut === 'en_attente' && isFuture && (
                  <>
                    <button className="btn-confirm-resa" onClick={() => handleConfirm(r.id)} disabled={confirming}>
                      ✅ Confirmer
                    </button>
                    <button className="btn-refuse-resa" onClick={() => handleRefuse(r.id)}>
                      ❌ Refuser
                    </button>
                  </>
                )}
                {!isProf && r.statut === 'en_attente' && (
                  <button className="btn-cancel-resa" onClick={() => handleCancel(r)}>
                    Annuler la demande
                  </button>
                )}
                {r.statut === 'confirmé' && isEndFuture && (
                  <button className="btn-cancel-resa" onClick={() => handleCancel(r)}>
                    Annuler le cours
                  </button>
                )}
              </div>

              {/* Footer */}
              <div className="resa-card-footer">
                <span>Demandé le {new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                {r.confirmed_at && <span>Confirmé le {new Date(r.confirmed_at).toLocaleDateString('fr-FR')}</span>}
              </div>
            </div>
          );
        })
      )}

      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
        reservation={selectedReservation}
      />
    </div>
  );
}
